/**
 * Provides a configured `SolandiaApi` to the tree.
 *
 * - On real SWA hosting → HTTP adapter (`/api`, identity injected by platform).
 * - Locally (no backend) → localStorage mock adapter, keyed to the signed-in
 *   mock user, seeded with demo data on first run.
 *
 * The component knows the current user (from the session) so the mock's
 * `currentUser` matches what `auth/me` will report. This keeps the whole app
 * consistent whether or not a backend exists.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  SolandiaApi,
  createHttpAdapter,
  createLocalStorageAdapter,
} from '@solandia/core';
import { isHostedOnSwa } from '../auth/authClient';
import type { SessionUser } from '../auth/types';
import { makeSeed } from './seed';

const ApiContext = createContext<SolandiaApi | null>(null);

export function ApiProvider({
  user,
  children,
}: {
  user: SessionUser;
  children: ReactNode;
}) {
  const api = useMemo(() => {
    if (isHostedOnSwa()) {
      // Cold-start resilience: generous timeout + retries so the first request
      // after the backend / SQL wake from idle recovers instead of looking stuck.
      return new SolandiaApi(
        createHttpAdapter({ baseUrl: '/api', timeoutMs: 65_000, retries: 3 }),
      );
    }
    const currentUser = {
      id: user.id,
      email: user.email,
      displayName: user.displayName,
    };
    return new SolandiaApi(
      createLocalStorageAdapter({
        currentUser,
        seed: makeSeed(currentUser),
      }),
    );
  }, [user.id, user.email, user.displayName]);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

export function useApi(): SolandiaApi {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi must be used within <ApiProvider>.');
  return api;
}
